"use client";

import { useEffect, useRef, useState } from "react";

type Props = { targetId: string };

/** Fixed top progress bar tracking scroll position through one element (the
 *  article body), not the whole page — 0% when its top enters view, 100%
 *  once its bottom has scrolled past the viewport. */
export function ArticleReadProgress({ targetId }: Props) {
  const [progress, setProgress] = useState(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;

    const update = () => {
      frame.current = null;
      const rect = target.getBoundingClientRect();
      const viewport = window.innerHeight;
      // Distance the content has to travel from "top just entering" to
      // "bottom just leaving" the viewport.
      const total = rect.height - viewport;
      const scrolled = -rect.top;
      const pct =
        total > 0 ? Math.min(100, Math.max(0, (scrolled / total) * 100)) : 0;
      setProgress(pct);
    };

    const onScroll = () => {
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [targetId]);

  return (
    <div
      role="progressbar"
      aria-label="Reading progress"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="fixed inset-x-0 top-0 z-40 h-1 bg-line/40"
    >
      <div
        className="h-full bg-primary transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
